import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Printer } from "@phosphor-icons/react";

export function QuoteDialog({ booking, onClose }) {
  if (!booking) return null;
  const print = () => {
    // Render into a sandboxed iframe via srcdoc instead of document.write, and
    // HTML-escape all user-supplied values inside quoteHtml() to prevent XSS.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    iframe.srcdoc = quoteHtml(booking);
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error("Print failed:", e);
      } finally {
        setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1000);
      }
    };
    document.body.appendChild(iframe);
  };
  return (
    <Dialog open={!!booking} onOpenChange={() => onClose()}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Generate Quote / Invoice</DialogTitle>
          <DialogDescription>Opens a print-ready document in a new tab. Choose “Save as PDF” in the print dialog.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div><span className="label-mono">Client</span><div>{booking.customer_name}</div></div>
          <div><span className="label-mono">Service</span><div className="capitalize">{booking.booking_type}{booking.booking_category ? ` · ${booking.booking_category}` : ""}</div></div>
          <div><span className="label-mono">Total</span><div className="font-heading text-2xl text-primary">${booking.total || booking.amount || 0}</div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={print} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="quote-print-button"><Printer size={14} className="mr-1.5" />Print / Save PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function quoteHtml(b) {
  const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const fmt = (d) => d ? esc(new Date(d).toLocaleString()) : "—";
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;
  return `<!doctype html><html><head><title>Quote · ${esc(b.customer_name)}</title>
<style>
  body{font-family:Georgia,serif;max-width:780px;margin:40px auto;padding:0 30px;color:#111}
  .head{display:flex;justify-content:space-between;border-bottom:2px solid #b48a3a;padding-bottom:16px;margin-bottom:24px}
  .brand{font-size:28px;letter-spacing:-0.02em;color:#b48a3a}
  .label{font-size:10px;letter-spacing:0.2em;color:#888;text-transform:uppercase;margin-bottom:4px}
  table{width:100%;border-collapse:collapse;margin:20px 0}
  td,th{padding:10px;border-bottom:1px solid #eee;text-align:left;font-size:14px}
  th{background:#f7f3eb;font-weight:600}
  .total{font-size:24px;color:#b48a3a;font-weight:700}
  .footer{font-size:12px;color:#888;border-top:1px solid #eee;padding-top:16px;margin-top:40px}
</style></head><body>
<div class="head">
  <div><div class="brand">01 AUTO GROUP</div><div style="font-size:11px;color:#777">01 Auto Group · Quote / Invoice</div></div>
  <div style="text-align:right"><div class="label">Date</div><div>${esc(new Date().toLocaleDateString())}</div></div>
</div>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px">
  <div><div class="label">Client</div><div style="font-size:18px">${esc(b.customer_name)}</div><div style="font-size:12px;color:#666">${esc(b.customer_email)}<br>${esc(b.customer_phone)}</div></div>
  <div style="text-align:right"><div class="label">Service</div><div style="font-size:18px;text-transform:capitalize">${esc(b.booking_type)}${b.booking_category?` · ${esc(b.booking_category)}`:""}</div></div>
</div>
<table>
  <tr><th>Detail</th><th>Information</th></tr>
  <tr><td>Vehicle</td><td>${esc(b.vehicle_label) || "—"} ${b.vehicle_plate?`(${esc(b.vehicle_plate)})`:""}</td></tr>
  <tr><td>Start</td><td>${fmt(b.start_datetime)}</td></tr>
  <tr><td>End</td><td>${fmt(b.end_datetime)}</td></tr>
  ${b.start_location?`<tr><td>Pickup</td><td>${esc(b.start_location)}</td></tr>`:""}
  ${b.end_location?`<tr><td>Dropoff</td><td>${esc(b.end_location)}</td></tr>`:""}
  ${b.in_between_stops?`<tr><td>Stops</td><td>${esc(b.in_between_stops)}</td></tr>`:""}
  ${b.driver_name?`<tr><td>Chauffeur</td><td>${esc(b.driver_name)}</td></tr>`:""}
  ${b.rental_rate?`<tr><td>Daily Rate</td><td>${money(b.rental_rate)}</td></tr>`:""}
  ${b.delivery?`<tr><td>Delivery</td><td>${money(b.delivery)}</td></tr>`:""}
  ${b.depo_sent?`<tr><td>Deposit Paid</td><td>${money(b.depo_sent)}</td></tr>`:""}
  ${b.remaining_balance?`<tr><td>Remaining</td><td>${money(b.remaining_balance)}</td></tr>`:""}
</table>
<div style="display:flex;justify-content:flex-end;align-items:baseline;gap:12px"><span class="label">Total</span><span class="total">${money(b.total || b.amount || 0)}</span></div>
${b.notes?`<div style="margin-top:24px"><div class="label">Notes</div><div style="font-size:13px;color:#555">${esc(b.notes)}</div></div>`:""}
<div class="footer">Thank you for choosing 01 Auto Group.</div>
</body></html>`;
}
